import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { ToastService } from '../../global/toast/toast.service';
import { MapSvgService } from '../../site-map/map-svg.service';
import { LotVM } from '../../site-map/models/lot-vm';
import { EffectiveFloorplan } from '../../utility/models/effective-floorplan';
import { ElevationVM } from '../../utility/models/elevation-vm';
import { FavoriteLotFit } from '../../utility/models/favorites-session';
import { NeighborhoodVM } from '../../utility/models/neighborhood-vm';
import { FavoritesService } from '../../utility/services/favorites.service';
import { LotFitService, SwingMappings } from '../../utility/services/lot-fit.service';
import { ViewModelFactoryService } from '../../utility/services/vm-factory.service';
import { GalleryImageVM } from '../gallery/gallery-image-vm';

@Component({
  selector: 'lot-fit-modal',
  templateUrl: './lot-fit-modal.component.html',
  styleUrls: ['./lot-fit-modal.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class LotFitModalComponent implements OnInit {
  @Input() lot: LotVM;
  @Input() mapSvg: SVGElement;
  @Input() neighborhood: NeighborhoodVM;
  @Input() showModal: boolean;
  @Input() lotFit: FavoriteLotFit;
  @Output() closeModal = new EventEmitter<void>();
  @ViewChild('svgContainer', { static: true }) svgContainer: ElementRef<HTMLDivElement>;

  floorplans: LotFitEffectiveFloorplan[] = [];
  selectedFloorplan: LotFitEffectiveFloorplan;
  selectedElevation: ElevationVM;
  selectedSwing: string;
  displayedImageOverride: { elevationId: number };
  isFavorited = false;

  constructor(
    private lotFitService: LotFitService,
    private mapSvgService: MapSvgService,
    private toaster: ToastService,
    private vmFactory: ViewModelFactoryService,
    private cdr: ChangeDetectorRef,
    private favService: FavoritesService
  ) {}

  ngOnInit(): void {
    if (this.mapSvg && this.lot) {
      if (this.lot.MappedStatus) {
        this.mapSvgService.colorLotsWithStatuses(
          [this.lot],
          [this.lot.MappedStatus],
          this.mapSvg,
          [this.lot.LotId],
          true
        );
      }

      this.mapSvgService.addSvgAndUpdateViewBoxToZoomInOnLot(
        this.lot,
        this.mapSvg,
        this.svgContainer.nativeElement,
        0.5
      );

      const swingMappings = this.lotFitService.getParsedSwingMappings(this.lot);
      this.floorplans = this.lot.MappedFloorPlanIds.map(fpInfo => {
        const fp = this.vmFactory.getEffectiveFloorplan(fpInfo.Id, null, {
          filteredElevationIds: this.lot.MappedElevationIds.map(x => x.Id),
          showAllElevationsIfFilterIsEmpty: false
        });

        if (fp) return new LotFitEffectiveFloorplan(fp, swingMappings?.get(fpInfo.Id));

        return null;
      }).filter(Boolean);

      const setFormFaves = this.trySetLotFitFromFavs();

      if (!setFormFaves) {
        this.setInitLotFitSelections(this.floorplans[0]);
      }

      this.cdr.markForCheck();
    }
  }

  setInitLotFitSelections(fp: LotFitEffectiveFloorplan) {
    this.selectedFloorplan = fp;
    if (fp.Elevations?.length) {
      this.selectedElevation = fp.Elevations[0];
      this.displayedImageOverride = { elevationId: fp.Elevations[0].ElevationId };
    }
    if (fp.GarageSwings?.left) {
      this.selectedSwing = 'L';
    } else if (fp.GarageSwings?.right) {
      this.selectedSwing = 'R';
    } else {
      this.selectedSwing = null;
    }
    this.lotFitService.changeVisibleFootPrint(
      this.lot,
      this.selectedFloorplan.ClientUniqueIdentifier,
      this.selectedSwing,
      this.selectedElevation.ClientUniqueIdentifier
    );
    this.checkFavsForSelections();
  }

  trySetLotFitFromFavs(): boolean {
    let foundFp: LotFitEffectiveFloorplan;
    let foundElev: ElevationVM;
    let foundSwing: string;
    let foundLotFitFav: FavoriteLotFit;

    if (this.lotFit) {
      foundLotFitFav = this.lotFit;
    }
    if (!foundLotFitFav) {
      foundLotFitFav = this.favService.current.LotFitFavorites.find(
        lf => lf.LotId === this.lot.LotId
      );
    }
    if (foundLotFitFav) {
      foundFp = this.floorplans.find(fp => fp.FloorplanId === foundLotFitFav.FloorplanId);
      if (foundFp) {
        foundElev = foundFp.Elevations?.find(el => el.ElevationId === foundLotFitFav.ElevationId);

        if (foundLotFitFav.Swing === 'L' && foundFp.GarageSwings.left) {
          foundSwing = foundLotFitFav.Swing;
        }
        if (foundLotFitFav.Swing === 'R' && foundFp.GarageSwings.right) {
          foundSwing = foundLotFitFav.Swing;
        }
      }
    }
    if (foundFp && foundElev && foundSwing) {
      this.selectedFloorplan = foundFp;
      this.selectedElevation = foundElev;
      this.selectedSwing = foundSwing;
      this.displayedImageOverride = { elevationId: this.selectedElevation.ElevationId };
      this.lotFitService.changeVisibleFootPrint(
        this.lot,
        this.selectedFloorplan.ClientUniqueIdentifier,
        this.selectedSwing,
        this.selectedElevation.ClientUniqueIdentifier
      );
      this.checkFavsForSelections();
      return true;
    }

    return false;
  }

  checkFavsForSelections() {
    if (
      this.favService.current.containsLotFitSelection(
        this.lot.LotId,
        this.selectedFloorplan.FloorplanId,
        this.selectedElevation.ElevationId,
        this.selectedSwing
      )
    ) {
      this.isFavorited = true;
    } else {
      this.isFavorited = false;
    }
  }

  handleFavsToggle() {
    const favLotFitSelection = new FavoriteLotFit({
      LotId: this.lot.LotId,
      FloorplanId: this.selectedFloorplan.FloorplanId,
      ElevationId: this.selectedElevation.ElevationId,
      Swing: this.selectedSwing,
      LotName: this.lot.Title,
      NeighborhoodId: this.lot.NeighborhoodId
    });
    if (this.isFavorited) {
      this.favService.removeLotFitSelection(favLotFitSelection);
    } else {
      this.favService.addLotFitSelection(favLotFitSelection);
    }
    this.checkFavsForSelections();
  }

  handleElevationSelect(elevation: ElevationVM) {
    this.selectedElevation = elevation;
    this.displayedImageOverride = { elevationId: elevation.ElevationId };
    this.lotFitService.changeVisibleFootPrint(
      this.lot,
      this.selectedFloorplan.ClientUniqueIdentifier,
      this.selectedSwing,
      this.selectedElevation.ClientUniqueIdentifier
    );
    this.checkFavsForSelections();
  }

  handleSwingSelect(swing: string) {
    this.selectedSwing = swing;
    this.lotFitService.changeVisibleFootPrint(
      this.lot,
      this.selectedFloorplan.ClientUniqueIdentifier,
      this.selectedSwing,
      this.selectedElevation.ClientUniqueIdentifier
    );
    this.checkFavsForSelections();
  }

  handleElevationChangeFromGallery(imageVM: GalleryImageVM) {
    const foundElev = this.selectedFloorplan.Elevations.find(
      elev => elev.ElevationId === imageVM.Id
    );
    if (foundElev) {
      this.selectedElevation = foundElev;
      this.lotFitService.changeVisibleFootPrint(
        this.lot,
        this.selectedFloorplan.ClientUniqueIdentifier,
        this.selectedSwing,
        this.selectedElevation.ClientUniqueIdentifier
      );
      this.checkFavsForSelections();
    }
  }

  handleLotFitModalClose() {
    this.closeModal.emit();
  }
}

class LotFitEffectiveFloorplan extends EffectiveFloorplan {
  GarageSwings: SwingMappings;

  constructor(fp: EffectiveFloorplan, swings: SwingMappings) {
    super();
    Object.assign(this, fp);
    this.GarageSwings = swings;
  }
}
